/**
 * TNK-140 phase A — 4-step stepper threaded through every page of the
 * import flow:
 *
 *   Configure → Upload → Review → Commit
 *
 *   /tools/imports/new            → Configure
 *   /tools/imports/$id            → Upload
 *   /tools/imports/$id/review     → Review
 *   /tools/imports/$id/results    → Commit
 *
 * TNK-140 phase E — recolored to the Figma indigo system + JetBrains
 * Mono step numerals. Loads the import-flow font CSS once here since
 * the stepper mounts on every import route.
 */
import { Check } from "lucide-react";
import { Link } from "@tanstack/react-router";

import "./fonts.css";
import { FIGMA } from "./figmaStyles";

export type ImportStep = "configure" | "upload" | "review" | "commit";

const STEPS: Array<{ key: ImportStep; label: string }> = [
  { key: "configure", label: "Configure" },
  { key: "upload", label: "Upload" },
  { key: "review", label: "Review" },
  { key: "commit", label: "Commit" },
];

// TNK-165 follow-up — done steps can navigate back so the operator can
// correct mistakes. Two rules keep this from "breaking the process":
//   1. "Configure" is never clickable. The configure page is
//      /tools/imports/new which creates a new draft; navigating back
//      to it would orphan the current draft, not edit it.
//   2. Once the current step is "commit" (results page), the import has
//      been persisted and the back-steps are read-only history. Don't
//      surface them as navigation — the wizard pages won't render in a
//      sensible state for a committed draft.
function stepHref(
  key: ImportStep,
  importId: string | undefined,
): {
  to: "/tools/imports/$id" | "/tools/imports/$id/review";
  importId: string;
} | null {
  if (!importId) return null;
  if (key === "upload") return { to: "/tools/imports/$id", importId };
  if (key === "review") return { to: "/tools/imports/$id/review", importId };
  return null;
}

export function ImportStepper({
  currentStep,
  importId,
}: {
  currentStep: ImportStep;
  importId?: string;
}) {
  const currentIdx = STEPS.findIndex((s) => s.key === currentStep);
  const backNavLocked = currentStep === "commit";
  return (
    <ol className="flex flex-wrap items-center gap-2">
      {STEPS.map((s, i) => {
        const isCurrent = i === currentIdx;
        const isDone = i < currentIdx;
        const href = stepHref(s.key, importId);
        const isClickable = isDone && !backNavLocked && href !== null;

        const dot = (
          <div
            className="flex h-5 w-5 items-center justify-center rounded-full"
            style={{
              background: isCurrent
                ? FIGMA.indigo
                : isDone
                  ? "rgba(99,102,241,0.25)"
                  : "rgba(255,255,255,0.06)",
              border: isCurrent
                ? "none"
                : isDone
                  ? `1px solid rgba(99,102,241,0.4)`
                  : `1px solid ${FIGMA.panelBorderHover}`,
            }}
          >
            {isDone ? (
              <Check size={10} color={FIGMA.indigoLighter} strokeWidth={2.5} />
            ) : (
              <span
                style={{
                  fontFamily: FIGMA.fontMono,
                  fontSize: 9,
                  fontWeight: 700,
                  color: isCurrent ? "#fff" : FIGMA.dim,
                }}
              >
                {i + 1}
              </span>
            )}
          </div>
        );

        const label = (
          <span
            style={{
              fontFamily: FIGMA.fontBody,
              fontSize: 12,
              fontWeight: isCurrent ? 600 : 400,
              color: isCurrent
                ? FIGMA.fg
                : isDone
                  ? FIGMA.indigoLight
                  : FIGMA.dim,
              textDecoration: isClickable ? "underline" : "none",
              textDecorationColor: "rgba(99,102,241,0.4)",
              textUnderlineOffset: 3,
            }}
          >
            {s.label}
          </span>
        );

        return (
          <li key={s.key} className="flex items-center gap-2">
            {isClickable && href ? (
              <Link
                to={href.to}
                params={{ id: href.importId }}
                className="flex items-center gap-2 rounded-md transition-opacity hover:opacity-80"
                aria-label={`Go back to ${s.label}`}
              >
                {dot}
                {label}
              </Link>
            ) : (
              <>
                {dot}
                {label}
              </>
            )}
            {i < STEPS.length - 1 && (
              <span
                className="mx-1 h-px w-6"
                style={{
                  background: isDone
                    ? "rgba(99,102,241,0.5)"
                    : "rgba(255,255,255,0.08)",
                }}
              />
            )}
          </li>
        );
      })}
    </ol>
  );
}
